import CustomInput from "@components/Forms/CustomInput";
import CustomSelect from "@components/UI/CustomSelect";
import { CloseUpdateIcon, ConfirmUpdateIcon, DeleteIcon, EditIcon } from "@components/Icons";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import TableWithPagination from "@components/Tables/TableWithPagination";
import AddAssetClassificationModal from "@components/UI/AddAssetClassificationModal";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import { getAssetImportantLevelOptions } from "@constants/policy";
import { PermissionContext } from "@contexts/PermissionContext";
import { useAssetClassification } from "@hooks/useAssetClassification";
import { useTheme } from "@hooks/useTheme";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CHARACTER_LENGTH } from "@constants/index";

export default function AssetClassificationList() {
  const { t } = useTranslation();
  const { canEditVulnerabilityPolicy } = useContext(PermissionContext);
  const { colors } = useTheme();
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteType, setDeleteType] = useState(""); // "single" or "multiple"

  const assetImportantLevelOptions = getAssetImportantLevelOptions(t);

  const {
    assetClassificationList,
    fetchAssetClassificationList,
    createAssetClassification,
    updateAssetClassification,
    deleteAssetClassification,
    isLoading,
    isUpdating,
    isCreating,
    isDeleting,
  } = useAssetClassification();

  const columns = canEditVulnerabilityPolicy()
    ? ["assetClassificationName", "importanceLevelCdName", "remark", "actions"]
    : ["assetClassificationName", "importanceLevelCdName", "remark"];

  const columnConfig = {
    assetClassificationName: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("policy.asset-classification-name"),
      customRender: (value, row, columnKey, index) => {
        if (editingRow === row.assetClassificationSeq) {
          return (
            <CustomInput
              type="text"
              value={editFormData.assetClassificationName || ""}
              onChange={(e) =>
                setEditFormData((prev) => ({
                  ...prev,
                  assetClassificationName: e.target.value,
                }))
              }
              placeholder={t("policy.write-asset-classification-name")}
              size="sm"
            />
          );
        }
        return value;
      },
    },
    importanceLevelCdName: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("policy.asset-importance-level"),
      customRender: (value, row, columnKey, index) => {
        if (editingRow === row.assetClassificationSeq) {
          // Find the current option for the value
          const currentValue = editFormData.importanceLevelCd || row.importanceLevelCd;
          const currentOption = assetImportantLevelOptions.find(option => option.value === currentValue);
          
          return (
            <div>
              <CustomSelect
                options={assetImportantLevelOptions}
                value={currentOption}
                onChange={(selectedOption) => setEditFormData((prev) => ({
                  ...prev,
                  importanceLevelCd: selectedOption ? selectedOption.value : (row.importanceLevelCd || ""),
                }))}
                placeholder={t("common.please-select")}
                size="sm"
                menuPlacement="bottom"
                menuPosition="fixed"
                clearable={false}
              />
            </div>
          );
        }
        // Translate the importance level value to its proper label
        const importanceLevelOption = assetImportantLevelOptions.find(option => option.value === row.importanceLevelCd);
        return importanceLevelOption ? importanceLevelOption.label : value;
      },
    },
    remark: {
      style: {
        width: "55%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.remark"),
      customRender: (value, row, columnKey, index) => {
        if (editingRow === row.assetClassificationSeq) {
          return (
            <>
            <CustomInput
              type="text"
              value={editFormData.remark || ""}
              onChange={(e) =>
                setEditFormData((prev) => ({
                  ...prev,
                  remark: e.target.value,
                }))
              }
              placeholder={t("policy.write-remark")}
              size="sm"
              maxLength={CHARACTER_LENGTH.REMARK}
            />
            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="asset-classification-list-remark-char-count">
              {(editFormData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
            </p>
            </>
          );
        }
        return value;
      },
    },
    actions: {
      style: {
        width: "100px",
        minWidth: "50px",
      },
      headerRender: () => (canEditVulnerabilityPolicy() ? t("common.action") : ""),
      customRender: (value, row, columnKey, index) => {
        if (!canEditVulnerabilityPolicy()) return null;

        if (editingRow === row.assetClassificationSeq) {
          return (
            <div className="flex items-center space-x-1">
              {editFormData?.assetClassificationName?.trim() !== "" && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSaveEdit(row);
                  }}
                  disabled={isUpdating}
                  className={`p-1 rounded ${colors.textActive}`}
                  title={t("common.save-button")}
                >
                  <ConfirmUpdateIcon className="h-6 w-6" />
                </button> 
              )}
              <div className={`w-px h-3 ${colors.bgCommunicationFilterButton}`}></div>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  handleCancelEdit();
                }}
                disabled={isUpdating}
                className="p-1 rounded text-red-600"
                title={t("common.cancel-button")}
              >
                <CloseUpdateIcon className="h-6 w-6" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center space-x-1">
            <button
              onClick={() => {handleEditAssetClassification(row)}}
              className={`p-1 rounded ${colors.textActive} ${colors.hover}`}
              title={t("common.edit-button")}
            >
              <EditIcon className="h-6 w-6" />
            </button>
            <div className={`w-px h-3 ${colors.bgCommunicationFilterButton}`}></div>
            <button
              onClick={() => {handleDeleteAssetClassification(row)}}
              className="p-1 rounded text-red-600"
              title={t("common.delete-button")}
            >
              <DeleteIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  };

  const handleEditAssetClassification = (rowData) => {
    setEditingRow(rowData.assetClassificationSeq);
    setEditFormData({
      assetClassificationName: rowData.assetClassificationName,
      importanceLevelCd: rowData.importanceLevelCd,
      remark: rowData.remark || "",
    });
  };

  const handleSaveEdit = async (rowData) => {
    try {
      const updateData = {
        assetClassificationName: editFormData.assetClassificationName.trim() || rowData.assetClassificationName,
        importanceLevelCd: editFormData.importanceLevelCd || rowData.importanceLevelCd,
        remark: editFormData.remark.trim() || "",
        db: rowData.db,
        delete: rowData.delete,
      };


      updateAssetClassification.mutate(
        { assetClassificationSeq: rowData.assetClassificationSeq, ...updateData },
        {
          onSuccess: () => {
            setEditingRow(null);
            setEditFormData({});
            fetchData(); // Refresh the data
          },
          onError: (error) => {
            console.error("Error updating asset classification:", error);
          },
        },
      );
    } catch (error) {
      console.error("Error saving edit:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditFormData({});
  };

  const handleAddAssetClassification = async (formData) => {
    try {
      createAssetClassification.mutate(formData, {
        onSuccess: () => {
          setShowAddModal(false);
          fetchData(); // Refresh the data
        },
      });
    } catch (error) {
      console.error("Error adding asset classification:", error);
    }
  };

  const handleDeleteAssetClassification = (rowData) => {
    setSelectedRows([rowData]);
    setDeleteType("single");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === "single") {
        // Single delete - use the first selected row
        const row = selectedRows[0];
        await deleteAssetClassification.mutateAsync([row.assetClassificationSeq]);
      } else if (deleteType === "multiple") {
        await deleteAssetClassification.mutateAsync(selectedRows)
      }
      
      setShowDeleteModal(false);
      setDeleteType("");
      setSelectedRows([]);
      fetchData(); // Refresh the data
    } catch (error) {
      setShowDeleteModal(false);
      if (error.response.status === 409) {
        setSelectedRows([]);
      }
      console.error("Error deleting asset classification:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteType("");
    setSelectedRows([]);
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      setDeleteType("multiple");
      setShowDeleteModal(true);
    }
  };

  const [searchCriteria, setSearchCriteria] = useState({
    assetClassificationName: "",
    importanceLevelCd: "",
    remark: "",
  });
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchCriteria);
  const debounceTimerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);

  const fetchData = () => {
    const bodyRequest = {
      assetClassificationName: debouncedSearchCriteria.assetClassificationName,
      importanceLevelCd: debouncedSearchCriteria.importanceLevelCd,
      remark: debouncedSearchCriteria.remark,
    };
    fetchAssetClassificationList(bodyRequest);
  };

  const handleSearch = useCallback((inputData) => {
    const value = inputData?.assetClassificationName;
    setSearchCriteria({
      assetClassificationName: value,
      importanceLevelCd: value,
      remark: value,
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchCriteria]);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hasActualChange = Object.values(searchCriteria).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchCriteria(searchCriteria);
      setIsSearching(false);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchCriteria]);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="asset-classification-list-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("policy.asset-classification-list")}</h1>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={() => {}} // No advanced search for now
          onDeleteProject={handleBulkDelete}
          onAdd={() => setShowAddModal(true)}
          searchField="assetClassificationName"
          searchValue={searchCriteria.assetClassificationName ?? ""}
          onSearch={handleSearch}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("policy.add-asset-classification")}
          titleDeleteButton={t("policy.delete-asset-classification")}
          isDisplayAddButton={canEditVulnerabilityPolicy()}
          isDisplayDeleteButton={canEditVulnerabilityPolicy()}
          isDisplayAdvancedSearch={false}
          isSearching={isSearching}
          searchInputTestId="asset-classification-list-search-input"
          addButtonTestId="asset-classification-list-add-button"
        />
      </div>

      {/* Asset Classification Table */}
      <div className="flex-grow min-h-0" data-testid="asset-classification-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={assetClassificationList}
          columns={columns}
          onRowClick={() => {}}
          loading={isLoading}
          totalPages={1}
          totalData={assetClassificationList.length}
          currentPage={1}
          setCurrentPage={() => {}}
          maxRowsPerPage={assetClassificationList.length}
          onCheckboxChange={handleCheckboxChange}
          selectedRows={selectedRows}
          disabledRow={[]}
          hasCheckbox={canEditVulnerabilityPolicy()}
          hasRadioButton={false}
          rowKey="assetClassificationSeq"
          shouldShowGoToPage={false}
          showPagination={false}
        />
      </div>

      {/* Add Asset Classification Modal */}
      <AddAssetClassificationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddAssetClassification}
        loading={isCreating}
      />

      {/* Delete Confirmation Modal */}
      {canEditVulnerabilityPolicy() && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={
            deleteType === "single"
              ? t("policy.delete-asset-classification")
              : t("policy.delete-asset-classification")
          }
          description={
            deleteType === "single"
              ? t("policy.delete-asset-classification-warning", { count: 1 })
              : t("policy.delete-asset-classification-warning", { count: selectedRows.length })
          }
          loading={isDeleting}
        />
      )}
    </div>
  );
}
